<script lang="ts" module>
  import type { HTMLActionArray } from "#lib"
  import type { BaseProps } from "#lib/internal/types"
  import type { VariantProps } from "class-variance-authority"
  import type { ClassValue } from "class-variance-authority/types"
  import type { Snippet } from "svelte"
  import type {
    HTMLAnchorAttributes,
    HTMLButtonAttributes,
  } from "svelte/elements"
  import { buttonVariants } from "."

  export type ButtonProps = Omit<
    BaseProps<"button"> & BaseProps<"a">,
    "class"
  > & {
    class?: ClassValue | null
    href?: HTMLAnchorAttributes["href"]
    type?: HTMLButtonAttributes["type"]
    variant: VariantProps<typeof buttonVariants>["variant"]
    size?: VariantProps<typeof buttonVariants>["size"]
    use?: HTMLActionArray
    ref?: HTMLAnchorElement | HTMLButtonElement | null
    target?: HTMLAnchorAttributes["target"]
    leftIcon?: Snippet
    rightIcon?: Snippet
    children?: Snippet
  }
</script>

<script lang="ts">
  import { useActions } from "#lib"
  import { cn } from "#lib/utils"
  import { buttonIconGapVariants, buttonIconVariants } from "."

  let {
    class: className = undefined,
    href = undefined,
    type = "button",
    variant,
    size = "large",
    ref = $bindable(),
    target = undefined,
    use = [],
    leftIcon,
    rightIcon,
    children,
    ...restProps
  }: ButtonProps = $props()

  const resolvedTarget = $derived(
    target !== undefined ? target : href ? "_blank" : undefined
  )

  // Gap only when an icon sits *next to* content. An icon-only button gets no
  // gap and no empty children node, so it centres on the button's own padding.
  const hasIcon = $derived(!!leftIcon || !!rightIcon)
  const needsGap = $derived(hasIcon && !!children)

  const iconClass = $derived(buttonIconVariants({ size }))
</script>

<!-- The icon/content blocks below run together on one line on purpose. Any
     whitespace between them becomes a text node inside the button, so a link
     button used inline mid-sentence renders "text ." instead of "text." -->
<svelte:element
  this={href ? "a" : "button"}
  type={href ? undefined : type}
  {href}
  class={cn(
    buttonVariants({
      variant,
      size,
      // Inside `className` so a caller's own gap-x still wins.
      className: cn(needsGap && buttonIconGapVariants({ size }), className),
    })
  )}
  use:useActions={use}
  {...restProps}
  role={href ? "link" : "button"}
  bind:this={ref}
  target={resolvedTarget}
  data-variant={variant}
>
  <!-- prettier-ignore -->
  {#if leftIcon}<span class={iconClass} data-button-icon="left">{@render leftIcon()}</span>{/if}{#if children}{@render children()}{/if}{#if rightIcon}<span
      class={iconClass}
      data-button-icon="right">{@render rightIcon()}</span
    >{/if}
</svelte:element>
